import React, { useState } from "react";

import { Button, Dialog } from "@fronterahealth/frontera-ui-components";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from "@fronterahealth/frontera-ui-components/ui/combobox";
import { Field, FieldDescription, FieldLabel } from "@fronterahealth/frontera-ui-components/ui/field";

import { ReportPageLocationEnums, useCreateReportTemplateSectionsMutation } from "@api/graphql/types-and-hooks";
import { GenerationTypeLabelMapping } from "@components/ConfirmGenerationDialog/ConfirmGenerationDialogV2";
import { useCanRegenerateReportParts } from "@components/RegenerateReportPart/useCanRegenerateReportParts";
import { notifyError } from "@components/notifications/notifications";

interface RegenerateReportButtonProps {
  reportId: string;
  disabled?: boolean;
  onRegenerated: () => void;
}

export const RegenerateReportButton: React.FC<RegenerateReportButtonProps> = ({
  reportId,
  disabled = false,
  onRegenerated,
}) => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const createReportTemplateSections = useCreateReportTemplateSectionsMutation({});
  const showRegenerate = useCanRegenerateReportParts();
  const anchor = useComboboxAnchor();

  const [sections, setSections] = useState<ReportPageLocationEnums[]>([]);

  const handleRegenerate = async () => {
    try {
      await Promise.all(
        sections.map((pageLocation) =>
          createReportTemplateSections.mutateAsync({
            input: { reportId, pageLocation: pageLocation, useConverter: true },
          }),
        ),
      );
      setDialogOpen(false);
      onRegenerated();
    } catch (err) {
      console.error("Failed to kickoff Report Part regeneration", err);
      notifyError("Something went wrong kicking off report regeneration");
    }
  };

  if (!showRegenerate) return null;

  return (
    <>
      <Button appearance="secondary" text="Regenerate Report" disabled={disabled} onClick={() => setDialogOpen(true)} />
      <Dialog
        size="xl"
        open={dialogOpen}
        setOpen={setDialogOpen}
        title="Confirm Report Regeneration"
        primaryButton={{
          text: createReportTemplateSections.isPending ? "Regenerating..." : "Regenerate",
          buttonAction: "destructive",
          onClick: handleRegenerate,
          disabled: createReportTemplateSections.isPending || !sections.length,
          className: "px-6",
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setDialogOpen(false),
          className: "px-6 hover:no-underline",
        }}
      >
        <Field>
          <FieldLabel htmlFor="country">Locations:</FieldLabel>
          <Combobox
            multiple
            autoHighlight
            items={[
              ReportPageLocationEnums.ReportPart_1,
              ReportPageLocationEnums.Recommendations,
              ReportPageLocationEnums.Conclusions,
              ReportPageLocationEnums.ReportPart_2,
              ReportPageLocationEnums.TreatmentPlan,
              ReportPageLocationEnums.Appendix,
            ].sort()}
            itemToStringValue={(value: ReportPageLocationEnums) => GenerationTypeLabelMapping[value]}
            value={sections}
            onValueChange={setSections}
          >
            {/* @ts-ignore: Ignoring the compiler and risking bugs because: @types/react (18) does not match actual version of react (19) */}
            <ComboboxChips ref={anchor}>
              <ComboboxValue>
                {(values) => (
                  <>
                    {values.map((value: ReportPageLocationEnums) => (
                      <ComboboxChip key={value}>{GenerationTypeLabelMapping[value]}</ComboboxChip>
                    ))}
                    <ComboboxChipsInput />
                  </>
                )}
              </ComboboxValue>
            </ComboboxChips>
            <ComboboxContent anchor={anchor}>
              <ComboboxEmpty>No items found.</ComboboxEmpty>
              <ComboboxList>
                {(item: ReportPageLocationEnums) => (
                  <ComboboxItem key={item} value={item}>
                    {GenerationTypeLabelMapping[item]}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
          <FieldDescription>
            If the section in selected page location was generated it would not generated again
          </FieldDescription>
        </Field>
      </Dialog>
    </>
  );
};
